import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Navbar from './Navbar';
import Footer from './Footer';

function PrivacyPolicy() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Helmet>
        <title>Privacy Policy - Lunar Electric | Solar Energy Solutions</title>
        <meta name="description" content="Privacy Policy for Lunar Electric - Learn how we collect, use, and protect your personal information for solar rooftop and renewable energy solutions." />
        <meta name="keywords" content="privacy policy, lunar electric privacy, data protection, solar company privacy" />
        <meta property="og:title" content="Privacy Policy - Lunar Electric" />
        <meta property="og:description" content="Privacy Policy for Lunar Electric - Learn how we collect, use, and protect your personal information." />
        <meta property="og:url" content="https://lunarelectric.in/privacy-policy" />
        <link rel="canonical" href="https://lunarelectric.in/privacy-policy" />
      </Helmet>

      <Navbar />

      <div style={{ 
        fontFamily: "'Poppins', sans-serif", 
        minHeight: '100vh', 
        backgroundColor: '#f5f5f5',
        padding: '2rem 1rem'
      }}>
        <div style={{ 
          maxWidth: '900px', 
          margin: '0 auto', 
          backgroundColor: 'white',
          padding: '3rem',
          borderRadius: '10px',
          boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
        }}>
          <h1 style={{ 
            color: '#1F3A93', 
            fontSize: '2.5rem',
            marginBottom: '1rem',
            textAlign: 'center'
          }}>
            Privacy Policy
          </h1>
          <p style={{ 
            textAlign: 'center', 
            color: '#666', 
            marginBottom: '3rem',
            fontSize: '1.1rem'
          }}>
            How We Collect and Use Your Information
          </p>

          <div style={{ lineHeight: '1.8', color: '#333' }}>
            
            <section style={{ marginBottom: '2rem' }}>
              <h2 style={{ color: '#1F3A93', fontSize: '1.5rem', marginBottom: '1rem' }}>
                1. Introduction
              </h2>
              <p style={{ marginBottom: '1rem' }}>
                Welcome to Lunar Electric ("we", "us", "our", "Company"). We are committed to protecting your privacy and ensuring the security of your personal information. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website or interact with our services related to solar rooftop installations and renewable energy solutions.
              </p>
              <p style={{ marginBottom: '1rem' }}>
                By using our website and providing your information, you consent to the collection and use of information in accordance with this Privacy Policy.
              </p>
            </section>

            <section style={{ marginBottom: '2rem' }}>
              <h2 style={{ color: '#1F3A93', fontSize: '1.5rem', marginBottom: '1rem' }}>
                2. Information We Collect
              </h2>
              <p style={{ marginBottom: '1rem' }}>
                We collect information that you voluntarily provide to us when you:
              </p>
              <ul style={{ paddingLeft: '2rem', marginBottom: '1rem' }}>
                <li>Fill out contact forms or request a callback</li>
                <li>Submit inquiries about our solar panel installation services</li>
                <li>Use our solar calculator or other tools</li>
                <li>Subscribe to our newsletter or updates</li>
                <li>Participate in surveys or promotional activities</li>
              </ul>
              <p style={{ marginBottom: '1rem' }}>
                The types of personal information we may collect include, but are not limited to:
              </p>
              <ul style={{ paddingLeft: '2rem', marginBottom: '1rem' }}>
                <li><strong>Contact Information:</strong> Name, email address, phone number, postal address</li>
                <li><strong>Property Information:</strong> Property location, roof type, energy consumption details, property size</li>
                <li><strong>Communication Preferences:</strong> Preferred method of contact and communication preferences</li>
                <li><strong>Technical Information:</strong> IP address, browser type, device information, website usage data</li>
              </ul>
              <p style={{ marginBottom: '1rem', fontStyle: 'italic', color: '#666' }}>
                <strong>Important:</strong> All information collected is voluntarily provided by you. We do not collect personal information without your explicit consent or action.
              </p>
            </section>

            <section style={{ marginBottom: '2rem' }}>
              <h2 style={{ color: '#1F3A93', fontSize: '1.5rem', marginBottom: '1rem' }}>
                3. How We Use Your Information
              </h2>
              <p style={{ marginBottom: '1rem' }}>
                We use the information you provide to us for the following purposes:
              </p>
              <ul style={{ paddingLeft: '2rem', marginBottom: '1rem' }}>
                <li><strong>To Contact You:</strong> We use your contact information (phone number, email address) to call you and reach out to you regarding your interest in our solar rooftop and renewable energy solutions</li>
                <li><strong>To Provide Services:</strong> To respond to your inquiries, provide quotes, schedule consultations, and deliver our solar installation services</li>
                <li><strong>To Improve Our Services:</strong> To understand your needs better and improve our website, services, and customer experience</li>
                <li><strong>To Send Communications:</strong> To send you updates about our services, solar energy tips, industry news, and promotional materials (you can opt-out at any time)</li>
                <li><strong>To Process Transactions:</strong> To process payments and manage your account if you become a customer</li>
                <li><strong>Legal Compliance:</strong> To comply with legal obligations and protect our rights and interests</li>
              </ul>
              <p style={{ marginBottom: '1rem' }}>
                <strong>Primary Purpose:</strong> The primary purpose of collecting your information is to enable us to contact you via phone calls, emails, and other communication channels to discuss and provide information about solar rooftop installations and other renewable energy solutions. This includes:
              </p>
              <ul style={{ paddingLeft: '2rem', marginBottom: '1rem' }}>
                <li>Calling you to discuss your solar energy requirements</li>
                <li>Providing customized quotes and proposals</li>
                <li>Scheduling site visits and consultations</li>
                <li>Following up on your inquiries and installations</li>
                <li>Offering post-installation support and maintenance services</li>
              </ul>
            </section>

            <section style={{ marginBottom: '2rem' }}>
              <h2 style={{ color: '#1F3A93', fontSize: '1.5rem', marginBottom: '1rem' }}>
                4. Information Sharing and Disclosure
              </h2>
              <p style={{ marginBottom: '1rem' }}>
                We do not sell, trade, or rent your personal information to third parties. We may share your information only in the following circumstances:
              </p>
              <ul style={{ paddingLeft: '2rem', marginBottom: '1rem' }}>
                <li><strong>Service Providers:</strong> We may share information with trusted third-party service providers who assist us in operating our website, conducting our business, or servicing you (e.g., hosting services, email service providers, payment processors)</li>
                <li><strong>Installation Partners:</strong> We may share necessary information with certified installation partners and technicians who will perform the solar installation at your property</li>
                <li><strong>Legal Requirements:</strong> We may disclose information when required by law, court order, or government regulation</li>
                <li><strong>Business Transfers:</strong> In the event of a merger, acquisition, or sale of assets, your information may be transferred as part of the business transaction</li>
                <li><strong>With Your Consent:</strong> We may share your information with other parties when you have given us explicit consent to do so</li>
              </ul>
              <p style={{ marginBottom: '1rem' }}>
                All third parties with whom we share information are required to maintain the confidentiality and security of your information.
              </p>
            </section>

            <section style={{ marginBottom: '2rem' }}>
              <h2 style={{ color: '#1F3A93', fontSize: '1.5rem', marginBottom: '1rem' }}>
                5. Data Security
              </h2>
              <p style={{ marginBottom: '1rem' }}>
                We implement appropriate technical and organizational security measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. However, no method of transmission over the Internet or electronic storage is 100% secure. While we strive to use commercially acceptable means to protect your information, we cannot guarantee absolute security.
              </p>
              <p style={{ marginBottom: '1rem' }}>
                Security measures include:
              </p>
              <ul style={{ paddingLeft: '2rem', marginBottom: '1rem' }}>
                <li>Encryption of sensitive data during transmission</li>
                <li>Secure storage systems with restricted access</li>
                <li>Regular security audits and updates</li>
                <li>Employee training on data protection</li>
              </ul>
            </section>

            <section style={{ marginBottom: '2rem' }}>
              <h2 style={{ color: '#1F3A93', fontSize: '1.5rem', marginBottom: '1rem' }}>
                6. Data Retention
              </h2>
              <p style={{ marginBottom: '1rem' }}>
                We retain your personal information for as long as necessary to fulfill the purposes outlined in this Privacy Policy, unless a longer retention period is required or permitted by law. We will retain and use your information to the extent necessary to comply with legal obligations, resolve disputes, and enforce our agreements.
              </p>
              <p style={{ marginBottom: '1rem' }}>
                If you become a customer, we may retain your information for the duration of our business relationship and for a reasonable period thereafter for warranty, support, and legal compliance purposes.
              </p>
            </section>

            <section style={{ marginBottom: '2rem' }}>
              <h2 style={{ color: '#1F3A93', fontSize: '1.5rem', marginBottom: '1rem' }}>
                7. Your Rights and Choices
              </h2>
              <p style={{ marginBottom: '1rem' }}>
                You have the following rights regarding your personal information:
              </p>
              <ul style={{ paddingLeft: '2rem', marginBottom: '1rem' }}>
                <li><strong>Access:</strong> You can request access to the personal information we hold about you</li>
                <li><strong>Correction:</strong> You can request correction of inaccurate or incomplete information</li>
                <li><strong>Deletion:</strong> You can request deletion of your personal information, subject to legal and contractual obligations</li>
                <li><strong>Objection:</strong> You can object to processing of your information for certain purposes</li>
                <li><strong>Opt-out:</strong> You can opt-out of marketing communications at any time by clicking the unsubscribe link in our emails or contacting us directly</li>
                <li><strong>Withdrawal of Consent:</strong> You can withdraw your consent for data processing at any time</li>
              </ul>
              <p style={{ marginBottom: '1rem' }}>
                To exercise any of these rights, please contact us using the contact information provided at the end of this Privacy Policy.
              </p>
            </section>

            <section style={{ marginBottom: '2rem' }}>
              <h2 style={{ color: '#1F3A93', fontSize: '1.5rem', marginBottom: '1rem' }}>
                8. Cookies and Tracking Technologies
              </h2>
              <p style={{ marginBottom: '1rem' }}>
                Our website may use cookies and similar tracking technologies to enhance your experience, analyze usage patterns, and improve our services. Cookies are small text files stored on your device when you visit our website.
              </p>
              <p style={{ marginBottom: '1rem' }}>
                You can control cookies through your browser settings. However, disabling cookies may affect the functionality of our website.
              </p>
            </section>

            <section style={{ marginBottom: '2rem' }}>
              <h2 style={{ color: '#1F3A93', fontSize: '1.5rem', marginBottom: '1rem' }}>
                9. Children's Privacy
              </h2>
              <p style={{ marginBottom: '1rem' }}>
                Our services are not directed to individuals under the age of 18. We do not knowingly collect personal information from children. If you believe we have inadvertently collected information from a child, please contact us immediately, and we will take steps to delete such information.
              </p>
            </section>

            <section style={{ marginBottom: '2rem' }}>
              <h2 style={{ color: '#1F3A93', fontSize: '1.5rem', marginBottom: '1rem' }}>
                10. Compliance with Indian Laws
              </h2>
              <p style={{ marginBottom: '1rem' }}>
                This Privacy Policy is designed to comply with applicable Indian laws and regulations, including:
              </p>
              <ul style={{ paddingLeft: '2rem', marginBottom: '1rem' }}>
                <li><strong>Information Technology Act, 2000</strong> and its rules</li>
                <li><strong>Information Technology (Reasonable Security Practices and Procedures and Sensitive Personal Data or Information) Rules, 2011</strong></li>
                <li><strong>Consumer Protection Act, 2019</strong></li>
              </ul>
              <p style={{ marginBottom: '1rem' }}>
                We are committed to protecting your sensitive personal data and information in accordance with Indian data protection laws.
              </p>
            </section>

            <section style={{ marginBottom: '2rem' }}>
              <h2 style={{ color: '#1F3A93', fontSize: '1.5rem', marginBottom: '1rem' }}>
                11. Third-Party Links
              </h2>
              <p style={{ marginBottom: '1rem' }}>
                Our website may contain links to third-party websites. We are not responsible for the privacy practices or content of these external sites. We encourage you to review the privacy policies of any third-party sites you visit.
              </p>
            </section>

            <section style={{ marginBottom: '2rem' }}>
              <h2 style={{ color: '#1F3A93', fontSize: '1.5rem', marginBottom: '1rem' }}>
                12. Changes to This Privacy Policy
              </h2>
              <p style={{ marginBottom: '1rem' }}>
                We may update this Privacy Policy from time to time to reflect changes in our practices, services, or legal requirements. We will notify you of any material changes by posting the new Privacy Policy on this page and updating the "Last Updated" date.
              </p>
              <p style={{ marginBottom: '1rem' }}>
                Your continued use of our website after any changes to this Privacy Policy constitutes your acceptance of the updated policy.
              </p>
            </section>

            <section style={{ marginBottom: '2rem' }}>
              <h2 style={{ color: '#1F3A93', fontSize: '1.5rem', marginBottom: '1rem' }}>
                13. Contact Us
              </h2>
              <p style={{ marginBottom: '1rem' }}>
                If you have any questions, concerns, or requests regarding this Privacy Policy or our data practices, please contact us:
              </p>
              <div style={{ 
                backgroundColor: '#f8f9fa', 
                padding: '1.5rem', 
                borderRadius: '5px',
                marginBottom: '1rem'
              }}>
                <p style={{ marginBottom: '0.5rem' }}>
                  <strong>Lunar Electric</strong><br />
                  Email: <a href="mailto:ronak@lunarelectric.in" style={{ color: '#1F3A93' }}>ronak@lunarelectric.in</a><br />
                  Phone: <a href="tel:+919148857417" style={{ color: '#1F3A93' }}>+91 9148857417</a>
                </p>
              </div>
              <p style={{ marginBottom: '1rem' }}>
                We will respond to your inquiries within a reasonable timeframe and address any concerns you may have regarding the collection, use, or disclosure of your personal information.
              </p>
            </section>

            <section style={{ 
              marginBottom: '2rem',
              padding: '1.5rem',
              backgroundColor: '#e7f3ff',
              borderRadius: '5px',
              border: '1px solid #1F3A93'
            }}>
              <h2 style={{ color: '#1F3A93', fontSize: '1.3rem', marginBottom: '1rem' }}>
                Your Consent
              </h2>
              <p style={{ color: '#333', marginBottom: '1rem' }}>
                By using our website and providing your personal information, you consent to the collection, use, and disclosure of your information as described in this Privacy Policy. You acknowledge that you have read and understood this Privacy Policy and agree to be bound by its terms.
              </p>
              <p style={{ color: '#333', marginBottom: '0' }}>
                If you do not agree with this Privacy Policy, please do not use our website or provide us with your personal information.
              </p>
            </section>

            <div style={{ 
              textAlign: 'center', 
              marginTop: '3rem',
              paddingTop: '2rem',
              borderTop: '2px solid #e0e0e0'
            }}>
              <p style={{ color: '#666', marginBottom: '1rem' }}>
                Last Updated: {new Date().toLocaleDateString('en-IN', { year: 'numeric', month: 'long', day: 'numeric' })}
              </p>
              <Link 
                to="/"
                style={{
                  color: '#1F3A93',
                  textDecoration: 'none',
                  fontSize: '1.1rem',
                  fontWeight: '600'
                }}
              >
                ← Back to Home
              </Link>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </>
  );
}

export default PrivacyPolicy;



